import { isEmpty, isUndefined, merge } from 'lodash-es';

import type { AnnotationsSlotsConfig } from './collect-annotations-slots.js';
import type { AnnotationsConfigProperties } from '../../../core/components/annotations/types/annotations-config-properties.js';
import { mergeAnnotationsConfig } from '../../../core/components/annotations/utils/merge-annotations-config.js';
import type { PointsDisplay } from '../../../core/components/canvas/types/canvas-shape.js';
import { isValidPointsDisplay } from '../../../core/components/canvas/utils/is-valid-points-display.js';
import {
  DEFAULT_INFINITE_ZOOM,
  DEFAULT_TRUNCATION_MODE,
} from '../../../core/constants.js';
import { isValidColorRule } from '../../../core/slots/ColorRule.js';
import {
  ColorPalette,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../../core/types/color-palette.js';
import type { TimeGapPolicy } from '../../../core/types/gap-policy.js';
import { ValueRepresentation } from '../../../core/types/scales.js';
import { SeriesCurve } from '../../../core/types/timeseries.js';
import { BaseToolbarProps } from '../../../core/types/toolbar.js';
import { isValidCSSColor } from '../../../core/utils/getValidColor.js';
import { getInteractions } from '../../../core/utils/interactions.js';
import { isValidColorPalette } from '../../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../../core/utils/is-valid-legend-position.js';
import { isValidSeriesDisplayMode } from '../../../core/utils/is-valid-series-display-mode.js';
import { isValidTooltipVariant } from '../../../core/utils/is-valid-tooltip-variant.js';
import { isValidValueRepresentation } from '../../../core/utils/is-valid-value-representation.js';
import { TimeseriesChartLegendProps } from '../../../timeseries/slots/Legend.js';
import { TimeseriesChartTooltipProps } from '../../../timeseries/slots/Tooltip.js';
import { TimeseriesChartXAxisProps } from '../../../timeseries/slots/XAxis.js';
import { TimeseriesChartYAxisProps } from '../../../timeseries/slots/YAxis.js';
import {
  DEFAULT_SCALE_FALLBACK_COLOR,
  defaultColorPalette,
  defaultGapPolicy,
  defaultLegend,
  defaultPointsDisplay,
  defaultSeriesCurve,
  defaultTimeseriesChartConfig,
  defaultToolbar,
  defaultTooltipVariant,
  defaultValueRepresentation,
  defaultYAxis,
} from '../constants.js';
import {
  TimeseriesChartConfigProperties,
  TimeseriesChartConfigProps,
  TimeseriesChartConfiguration,
  TimeseriesChartLegendConfig,
  TimeseriesChartSlots,
  TimeseriesChartThresholdConfig,
  TimeseriesChartThresholdsConfig,
  TimeseriesChartTooltipConfig,
  TimeseriesChartXAxisConfig,
  TimeseriesChartYAxisConfig,
  TimeseriesZoomDomain,
} from '../types/timeseries-config.js';
import type { TimeseriesChartThresholdIndicatorProps } from '../types/timeseries.js';
import {
  TimeseriesChartToolbarConfig,
  TimeseriesChartToolbarSlotProps,
  TimeseriesToolbarInternalProps,
} from '../types/toolbar.js';

/**
 * Adoption-local port of the legacy `createTimeseriesConfiguration` engine
 * used by `useTimeseriesChartConfig`, kept independent from
 * `../../../timeseries`. Only the config-resolution logic (merging context +
 * slots + defaults) is duplicated here; the export-side flattening lives in
 * `./serialize-timeseries-config.js`.
 */

const sanitizeYAxisConfig = (
  yAxes: TimeseriesChartYAxisProps[],
): TimeseriesChartYAxisConfig => {
  const reversedYAxes = yAxes.reverse();
  const leftAxis = reversedYAxes.find(
    (axis) => axis?.position === 'left' || axis?.position === undefined,
  );
  const rightAxis = reversedYAxes.find((axis) => axis?.position === 'right');
  const sanitizedYAxes: TimeseriesChartYAxisConfig = {};

  if (leftAxis) {
    sanitizedYAxes.left = leftAxis;
  }
  if (rightAxis) {
    sanitizedYAxes.right = rightAxis;
  }

  return sanitizedYAxes;
};

export function getYAxisConfiguration(
  slotsYAxis: TimeseriesChartYAxisProps[],
  contextYAxis: TimeseriesChartYAxisProps[] | undefined,
): TimeseriesChartYAxisConfig {
  const contextConfig =
    contextYAxis === undefined ? {} : sanitizeYAxisConfig(contextYAxis);
  const slotsConfig =
    slotsYAxis === undefined ? {} : sanitizeYAxisConfig(slotsYAxis);

  const { left, right } = merge({}, contextConfig, slotsConfig);

  if (left === undefined && right === undefined) {
    return defaultYAxis;
  }

  const result: TimeseriesChartYAxisConfig = {};

  if (left) {
    result.left = merge({}, defaultYAxis.left, left);
  }

  if (right) {
    const defaultRight = { ...defaultYAxis.left, position: 'right' };
    result.right = merge({}, defaultRight, right);
  }

  return result;
}

export function getLegendConfiguration(
  contextLegend: TimeseriesChartLegendProps | undefined,
  slotsLegend: TimeseriesChartLegendProps,
): TimeseriesChartLegendConfig {
  const legendConfig = merge(
    {},
    defaultLegend,
    contextLegend,
    slotsLegend,
  ) as TimeseriesChartLegendConfig;

  legendConfig.position = isValidLegendPosition(legendConfig.position)
    ? legendConfig.position
    : defaultLegend.position;

  return legendConfig;
}

export function getTooltipConfiguration(
  contextTooltip: TimeseriesChartTooltipProps | undefined,
  slotsTooltip: TimeseriesChartTooltipProps,
): TimeseriesChartTooltipConfig {
  const tooltip = merge(
    {},
    { variant: defaultTooltipVariant },
    contextTooltip,
    slotsTooltip,
  ) as TimeseriesChartTooltipConfig;

  tooltip.variant = isValidTooltipVariant(tooltip.variant)
    ? tooltip.variant
    : defaultTooltipVariant;

  if (tooltip.seriesDisplayMode) {
    tooltip.seriesDisplayMode = isValidSeriesDisplayMode(
      tooltip.seriesDisplayMode,
    )
      ? tooltip.seriesDisplayMode
      : 'multi-line';
  } else {
    tooltip.seriesDisplayMode =
      tooltip.variant === 'shared' ? 'single-line' : 'multi-line';
  }

  return tooltip;
}

export function getXAxisConfiguration(
  contextXAxis: TimeseriesChartXAxisProps | undefined,
  slotsXAxis: TimeseriesChartXAxisProps | undefined,
): TimeseriesChartXAxisConfig {
  return merge({}, contextXAxis, slotsXAxis);
}

export function getAnnotationsConfiguration(
  contextAnnotations: AnnotationsConfigProperties | undefined,
  slotsAnnotations: AnnotationsSlotsConfig | undefined,
): AnnotationsSlotsConfig | undefined {
  return mergeAnnotationsConfig(contextAnnotations, slotsAnnotations);
}

export function getThresholdsConfiguration(
  contextThresholds: TimeseriesChartThresholdConfig[] = [],
  slotsThresholds: TimeseriesChartThresholdIndicatorProps[] = [],
): TimeseriesChartThresholdsConfig | undefined {
  const allThresholds = contextThresholds
    .concat(slotsThresholds)
    .map((threshold) => ({
      ...threshold,
      id: crypto.randomUUID(),
      position: !threshold.position ? 'left' : threshold.position,
      color: isValidCSSColor(threshold.color)
        ? threshold.color
        : DEFAULT_SCALE_FALLBACK_COLOR,
    }));

  if (!allThresholds.length) {
    return undefined;
  }

  const thresholdConfig: TimeseriesChartThresholdsConfig = {};

  thresholdConfig.left = allThresholds.filter(
    (threshold) => threshold.position === 'left',
  );

  thresholdConfig.right = allThresholds.filter(
    (threshold) => threshold.position === 'right',
  );

  return thresholdConfig;
}

const isUndefinedOrIsEmpty = (value?: { enabled?: boolean }) => {
  return isUndefined(value) && isEmpty(value);
};

/**
 * Processes toolbar config to handle the hidden property.
 * If hidden is true, it overrides enabled and sets it to false.
 */
const processToolbarConfig = (
  config: TimeseriesChartToolbarConfig | BaseToolbarProps | undefined,
): TimeseriesChartToolbarConfig | undefined => {
  if (!config) {
    return config;
  }

  // Need to cast to access hidden property
  const { hidden, ...rest } = config as TimeseriesChartToolbarConfig & {
    hidden?: boolean;
  };

  if (hidden === true) {
    return { ...rest, enabled: false };
  }
  return config;
};

export function getToolbar(
  contextToolbar: TimeseriesChartToolbarConfig | undefined,
  slotsChartToolbar: TimeseriesChartToolbarSlotProps | undefined,
  slotDownloadCSV?: { enabled?: boolean },
  contextDownloadCSV?: { enabled?: boolean },
): TimeseriesToolbarInternalProps {
  const hasNoDownloadData =
    isUndefinedOrIsEmpty(slotDownloadCSV) &&
    isUndefinedOrIsEmpty(contextDownloadCSV);

  if (!contextToolbar && isEmpty(slotsChartToolbar) && hasNoDownloadData) {
    return { ...defaultToolbar };
  }

  const processedContextToolbar = processToolbarConfig(contextToolbar);
  const processedSlotsToolbar = processToolbarConfig(slotsChartToolbar);

  return merge(
    {},
    defaultToolbar,
    processedContextToolbar,
    processedSlotsToolbar,
    {
      downloadData: slotDownloadCSV ?? contextDownloadCSV,
    },
  );
}

export function getGapPolicyConfiguration(
  contextGapPolicy?: TimeGapPolicy,
  propsGapPolicy?: TimeGapPolicy,
) {
  return propsGapPolicy ?? contextGapPolicy ?? defaultGapPolicy;
}

export function getPointsDisplayConfiguration(
  contextPointsDisplay?: PointsDisplay,
  propsPointsDisplay?: PointsDisplay,
) {
  const pointsDisplay =
    propsPointsDisplay ?? contextPointsDisplay ?? defaultPointsDisplay;

  return isValidPointsDisplay(pointsDisplay)
    ? pointsDisplay
    : defaultPointsDisplay;
}

export function getValueRepresentationConfiguration(
  contextValueRepresentation?: ValueRepresentation,
  propsValueRepresentation?: ValueRepresentation,
) {
  const valueRepresentation =
    propsValueRepresentation ??
    contextValueRepresentation ??
    defaultValueRepresentation;

  return isValidValueRepresentation(valueRepresentation)
    ? valueRepresentation
    : defaultValueRepresentation;
}

export function getColorPaletteConfiguration(
  contextColorPalette?: ColorPalette | CustomColorPalette,
  propsColorPalette?: ColorPalette | CustomColorPalette,
) {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? defaultColorPalette;

  return isCustomColorPalette(colorPalette) || isValidColorPalette(colorPalette)
    ? colorPalette
    : defaultTimeseriesChartConfig.colorPalette ?? defaultColorPalette;
}

export function getSeriesCurveConfiguration(
  contextConfig?: SeriesCurve,
  propsConfig?: SeriesCurve,
) {
  return propsConfig ?? contextConfig ?? defaultSeriesCurve;
}

export function getInfiniteZoomConfiguration(
  contextConfig?: boolean,
  propsConfig?: boolean,
) {
  return propsConfig ?? contextConfig ?? DEFAULT_INFINITE_ZOOM;
}

export function getInitialZoomConfiguration(
  contextConfig?: TimeseriesZoomDomain,
  propsConfig?: TimeseriesZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

export function getCurrentZoomConfiguration(
  contextConfig?: TimeseriesZoomDomain,
  propsConfig?: TimeseriesZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

/**
 * Creates a timeseries Configuration merging the configuration provided in
 * the context config, slots, and defaults.
 */
export function createTimeseriesConfiguration(
  slots: TimeseriesChartSlots,
  contextConfig: TimeseriesChartConfigProperties,
  timeseriesChartConfigProps: TimeseriesChartConfigProps,
): TimeseriesChartConfiguration {
  const yAxis = getYAxisConfiguration(slots.yAxis, contextConfig.yAxis);
  const truncationMode =
    timeseriesChartConfigProps?.truncationMode ??
    contextConfig.truncationMode ??
    DEFAULT_TRUNCATION_MODE;

  const legend = getLegendConfiguration(contextConfig.legend, slots.legend);
  const tooltip = getTooltipConfiguration(contextConfig.tooltip, slots.tooltip);
  const xAxis = getXAxisConfiguration(contextConfig.xAxis, slots.xAxis);
  const configThresholds = contextConfig.thresholdIndicators || [];
  const thresholds = getThresholdsConfiguration(
    configThresholds,
    slots.thresholds,
  );

  const toolbar = getToolbar(contextConfig.toolbar, slots.toolbar);

  const propsInteractions = {
    onZoomChange: timeseriesChartConfigProps.onZoomChange,
  };

  const hasContextInteractions =
    contextConfig && (contextConfig.pan || contextConfig.zoom);

  const chartConfigInteractions = hasContextInteractions
    ? {
        pan: { enabled: contextConfig.pan?.disabled !== true },
        zoom: { enabled: contextConfig.zoom?.disabled !== true },
        zoomX: { enabled: contextConfig.zoom?.disabled !== true },
      }
    : {};

  const interactions = getInteractions(
    contextConfig.interactions,
    slots.interactions,
    propsInteractions,
    chartConfigInteractions,
  );

  const gapPolicy = getGapPolicyConfiguration(
    contextConfig.gapPolicy,
    timeseriesChartConfigProps?.gapPolicy,
  );
  const pointsDisplay = getPointsDisplayConfiguration(
    contextConfig.pointsDisplay,
    timeseriesChartConfigProps?.pointsDisplay,
  );
  const valueRepresentation = getValueRepresentationConfiguration(
    contextConfig.valueRepresentation,
    timeseriesChartConfigProps?.valueRepresentation,
  );
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    timeseriesChartConfigProps?.colorPalette,
  );

  const curve = getSeriesCurveConfiguration(
    contextConfig.curve,
    timeseriesChartConfigProps?.curve,
  );

  const infiniteZoom = getInfiniteZoomConfiguration(
    contextConfig.infiniteZoom,
    timeseriesChartConfigProps.infiniteZoom,
  );

  const initialZoom = getInitialZoomConfiguration(
    contextConfig.initialZoom,
    timeseriesChartConfigProps.initialZoom,
  );

  const currentZoom = getCurrentZoomConfiguration(
    contextConfig.currentZoom,
    timeseriesChartConfigProps.currentZoom,
  );

  const select = timeseriesChartConfigProps.select ?? slots.select;

  const colorRules = (contextConfig.colorRules ?? [])
    .filter(isValidColorRule)
    .concat(slots.colorRules ?? []);

  return {
    gapPolicy,
    pointsDisplay,
    valueRepresentation,
    colorPalette,
    legend,
    tooltip,
    toolbar,
    xAxis,
    yAxis,
    annotations: getAnnotationsConfiguration(
      contextConfig.annotations,
      slots.annotations,
    ),
    truncationMode,
    thresholds,
    interactions,
    initialZoom,
    currentZoom,
    infiniteZoom,
    curve,
    select,
    colorRules,
  };
}

// Regex for validating dates like '2022-07-28T00:00:00Z' or '2022-07-28T00:00:00.000Z'
// (the latter is what JSON.stringify(new Date()) - and so getConfig() - always produces)
const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

function reviver(key: string, value: string) {
  if (typeof value === 'string' && dateFormat.test(value)) {
    return new Date(value);
  }

  return value;
}

/**
 * Deserializes a Timeseries string config
 */
export function deserializeConfig(
  serializedConfig: string,
): TimeseriesChartConfigProperties {
  try {
    return merge(
      {},
      defaultTimeseriesChartConfig,
      JSON.parse(serializedConfig, reviver),
    );
  } catch {
    return defaultTimeseriesChartConfig;
  }
}
